import { Outlet, useNavigate, useRoutes, Navigate, useLocation } from 'react-router-dom';
import { Cookies } from 'react-cookie';
import { useEffect, lazy, Suspense } from 'react';
import { initReactI18next } from 'react-i18next';
import i18next from 'i18next';

import { useDispatch, useSelector } from 'react-redux';
import { setRole } from './redux/auth.slice';
import { fetchMe } from './redux/me.slice';
import translations from './utils/translations.json';

// layouts (keep eager - always needed)
import DashboardLayout from './layouts/dashboard';
import SimpleLayout from './layouts/simple';
import ProtectedRoute from './ProtectedRoute';

// i18next ni komponent tashqarisida bir marta init qilish
i18next.use(initReactI18next).init({
  resources: translations,
  lng: localStorage.getItem('language') || 'uz',
});

const cookie = new Cookies();

// lazy-loaded pages
const LoginPage = lazy(() => import('./pages/LoginPage'));
const Page404 = lazy(() => import('./pages/Page404'));
const DashboardAppPage = lazy(() => import('./pages/DashboardAppPage'));
const ClientsPage = lazy(() => import('./pages/ClientsPage/ClientsPage'));
const ProductsPage = lazy(() => import('./pages/Products'));
const OrdersPage = lazy(() => import('./pages/OrdersPage'));
const MonitoringPage = lazy(() => import('./pages/MonitoringPage'));
const CouriersPage = lazy(() => import('./pages/CouriersPage'));
const NotesPage = lazy(() => import('./pages/NotesPage'));
const SettingsPage = lazy(() => import('./pages/SettingsPage'));
const VendorsPage = lazy(() => import('./pages/VendorsPage'));
const VendorDetail = lazy(() => import('./pages/VendorsPage/VendorDetail'));
const PartnersPage = lazy(() => import('./pages/PartnersPage'));
const Banner = lazy(() => import('./pages/Banners'));
const Promotions = lazy(() => import('./pages/Promotions'));
const SMSPage = lazy(() => import('./pages/SMSPage'));

function LoadingFallback() {
  return (
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
      <div style={{ width: 40, height: 40, border: '4px solid #e0e0e0', borderTop: '4px solid #1976d2', borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

export default function Router() {
  const [navigate, location, dispatch] = [useNavigate(), useLocation(), useDispatch()];
  const { language } = useSelector((state) => state.account);
  const { role } = useSelector((state) => state.auth);
  const accessToken = cookie.get('access_token');

  useEffect(() => {
    if (language && language !== i18next.language) {
      i18next.changeLanguage(language);
    }
  }, [language]);

  useEffect(() => {
    if (accessToken) {
      dispatch(fetchMe());
    }
  }, [role, accessToken, dispatch]);

  useEffect(() => {
    const savedRole = cookie.get('role');
    if (savedRole) dispatch(setRole(savedRole));
    const accessToken = cookie.get('access_token');

    if (!accessToken && location.pathname !== '/login') navigate('/login', { replace: true });
  }, []);

  const rs = useRoutes([
    {
      path: '/dashboard',
      element: <DashboardLayout />,
      children: [
        { element: <Navigate to="/dashboard/app" />, index: true },
        {
          path: 'app',
          element: (
            <ProtectedRoute allowedRoles={['ADMIN', 'VENDOR']}>
              <DashboardAppPage />
            </ProtectedRoute>
          ),
        },
        {
          path: 'vendors',
          element: (
            <ProtectedRoute allowedRoles={['ADMIN']}>
              <Outlet />
            </ProtectedRoute>
          ),
          children: [
            { element: <Navigate to="/dashboard/vendors/all" />, index: true },
            {
              path: 'all',
              element: <VendorsPage />,
            },
            {
              path: 'edit/:vendorId',
              element: <VendorDetail />,
            },
            {
              path: 'add',
              element: <div>Add</div>,
            },
          ],
        },
        {
          path: 'partners',
          element: (
            <ProtectedRoute allowedRoles={['ADMIN']}>
              <Outlet />
            </ProtectedRoute>
          ),
          children: [
            { element: <Navigate to="/dashboard/partners/all" />, index: true },
            {
              path: 'all',
              element: <PartnersPage />,
            },
          ],
        },
        {
          path: 'products',
          element: (
            <ProtectedRoute allowedRoles={['ADMIN']}>
              <Outlet />
            </ProtectedRoute>
          ),
          children: [
            { element: <Navigate to="/dashboard/products/all" />, index: true },
            {
              path: 'all',
              element: <ProductsPage />,
            },
            {
              path: 'edit/:productId',
              element: <div>edit</div>,
            },
            {
              path: 'add',
              element: <div>Add</div>,
            },
          ],
        },
        {
          path: 'clients',
          element: (
            <ProtectedRoute allowedRoles={['ADMIN', 'VENDOR']}>
              <Outlet />
            </ProtectedRoute>
          ),
          children: [
            { element: <Navigate to="/dashboard/clients/all" />, index: true },
            {
              path: 'all',
              element: <ClientsPage />,
            },
            {
              // Qarzdorlar endi Mijozlar sahifasidagi tab orqali ochiladi;
              // eski manzil shu tabga yo'naltiriladi.
              path: 'debtorclients',
              element: <Navigate to="/dashboard/clients/all?tab=debtors" replace />,
            },
            {
              path: 'edit/:clientId',
              element: <div>edit</div>,
            },
            {
              path: 'add',
              element: <div>Add</div>,
            },
          ],
        },
        {
          path: 'orders',
          element: (
            <ProtectedRoute allowedRoles={['VENDOR', 'COURIER']}>
              <Outlet />
            </ProtectedRoute>
          ),
          children: [
            { element: <Navigate to="/dashboard/orders/all" />, index: true },
            {
              path: 'all',
              element: <OrdersPage />,
            },
            {
              path: 'archive',
              element: <OrdersPage archive />,
            },
          ],
        },
        {
          path: 'monitoring',
          element: <Outlet />,
          children: [
            { element: <Navigate to="/dashboard/monitoring/all" />, index: true },
            {
              path: 'all',
              element: <MonitoringPage />,
            },
          ],
        },
        {
          path: 'couriers',
          element: (
            <ProtectedRoute allowedRoles={['VENDOR']}>
              <Outlet />
            </ProtectedRoute>
          ),
          children: [
            { element: <Navigate to="/dashboard/couriers/all" />, index: true },
            {
              path: 'all',
              element: <CouriersPage />,
            },
            {
              path: 'edit/:clientId',
              element: <div>edit</div>,
            },
            {
              path: 'add',
              element: <div>Add</div>,
            },
          ],
        },
        {
          path: 'settings',
          element: (
            <ProtectedRoute allowedRoles={['VENDOR']}>
              <Outlet />
            </ProtectedRoute>
          ),
          children: [
            { element: <Navigate to="/dashboard/settings/all" />, index: true },
            {
              path: 'all',
              element: <SettingsPage />,
            },
          ],
        },
        {
          path: 'notes',
          element: (
            <ProtectedRoute allowedRoles={['ADMIN', 'VENDOR']}>
              <Outlet />
            </ProtectedRoute>
          ),
          children: [
            { element: <Navigate to="/dashboard/notes/all" />, index: true },
            {
              path: 'all',
              element: <NotesPage />,
            },
          ],
        },

        {
          path: 'banners',
          element: (
            <ProtectedRoute allowedRoles={['ADMIN']}>
              <Outlet />
            </ProtectedRoute>
          ),
          children: [
            { element: <Navigate to="/dashboard/banners/all" />, index: true },
            {
              path: 'all',
              element: <Banner />,
            },
          ],
        },
        {
          path: 'promotions',
          element: (
            <ProtectedRoute allowedRoles={['VENDOR']}>
              <Outlet />
            </ProtectedRoute>
          ),
          children: [
            { element: <Navigate to="/dashboard/promotions/all" />, index: true },
            {
              path: 'all',
              element: <Promotions />,
            },
          ],
        },
        {
          path: 'sms',
          element: (
            <ProtectedRoute allowedRoles={['VENDOR']}>
              <SMSPage />
            </ProtectedRoute>
          ),
        },
      ],
    },
    {
      path: 'login',
      element: <LoginPage />,
    },
    {
      element: <SimpleLayout />,
      children: [
        { element: <Navigate to="/dashboard/app" />, index: true },
        { path: '404', element: <Page404 /> },
        { path: '*', element: <Navigate to="/404" /> },
      ],
    },
    {
      path: '*',
      element: <Navigate to="/404" replace />,
    },
  ]);

  return <Suspense fallback={<LoadingFallback />}>{rs}</Suspense>;
}
